"use client";
import PlayButton from "@/components/shared/video/components/play-button";
import { CarouselApi, CarouselContent, CarouselItem, useCarousel } from "@/components/ui/carousel";
import { useGlobalCtx } from "@/contexts/global";
import { useWindowSize } from "@/hooks/use-window-size";
import { cn } from "@/lib/utils";
import { EmblaEventType } from "embla-carousel";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { VideoComponent } from "./video";
import { PerformanceProps } from "@/services/home/types";
import { Reveal } from "@/components/ui/animations";

const TWEEN_FACTOR_BASE = 0.84;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

type Props = {
  data: PerformanceProps[];
};

export default function PerformanceContents({ data }: Readonly<Props>) {
  const { currentPlayingVideo, setCurrentPlayingVideo } = useGlobalCtx();
  const { api: emblaApi } = useCarousel();
  const tweenFactor = useRef(0);
  const { width } = useWindowSize();
  const [itemsInView, setItemsInView] = useState(3);

  const setTweenFactor = useCallback((emblaApi: CarouselApi) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi!.scrollSnapList().length;
  }, []);

  const tweenOpacity = useCallback(
    (emblaApi: CarouselApi, eventName?: EmblaEventType) => {
      if (!emblaApi) return;

      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === "scroll";

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue = itemsInView - Math.abs(diffToTarget * tweenFactor.current);

          const opacity = numberWithinRange(tweenValue, 0, 1).toString();
          emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
        });
      });
    },
    [itemsInView]
  );

  useEffect(() => {
    if (width < 1024) {
      setItemsInView(1);
      return;
    }

    if (width < 1280) {
      setItemsInView(2);
      return;
    }

    setItemsInView(3);
  }, [width]);

  useEffect(() => {
    if (!emblaApi) return;

    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);
    emblaApi
      .on("reInit", setTweenFactor)
      .on("reInit", tweenOpacity)
      .on("scroll", tweenOpacity)
      .on("slideFocus", tweenOpacity);
  }, [emblaApi, tweenOpacity]);

  const cardGradientId = useId();

  return (
    <CarouselContent className="relatice 3xl:gap-6">
      {data.map((perf, index) => (
        <CarouselItem
          key={index}
          onClick={() => {
            if (width < 700) return;

            const itInView = emblaApi?.slidesInView() ?? [];

            if (itInView?.includes(index) && index === itInView[itInView.length - 1]) {
              emblaApi?.scrollNext();
              return;
            }

            if (itInView?.length > itemsInView + 1 && index === itInView[itInView.length - 2]) {
              emblaApi?.scrollNext();
              return;
            }

            if (index === 0) {
              emblaApi?.scrollTo(index);
            }
          }}
          className="embla__slide sm:basis-8/12 lg:basis-5/12 xl:basis-1/4"
        >
          <Reveal
            delay={index * 0.1}
            className="select-none p-1 transition-all duration-150 ease-in hover:scale-105 sm:basis-8/12"
          >
            <div className="relative flex flex-col gap-6 rounded-[2.083vw] bg-[rgba(58,40,92,0.22)] px-4 pb-8 pt-4 backdrop-blur-[17px] 2xl:gap-12">
              <svg
                className="pointer-events-none absolute inset-0 z-0 h-full w-full"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
              >
                <defs>
                  <linearGradient
                    id={cardGradientId}
                    x1="840"
                    y1="0"
                    x2="840"
                    y2="436"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#F0DFC1" />
                    <stop offset="0.791452" stopColor="#C89452" />
                    <stop offset="1" stopColor="#B07F44" />
                  </linearGradient>
                </defs>
                <rect
                  x="0.5"
                  y="0.5"
                  width="calc(100% - 1px)"
                  height="calc(100% - 1px)"
                  rx="2.083vw"
                  ry="2.083vw"
                  fill="none"
                  stroke={`url(#${cardGradientId})`}
                  strokeWidth="1"
                />
              </svg>

              <div className="relative z-10 flex flex-col gap-6 2xl:gap-12">
                <div className="relative">
                  <div className="relative h-[220px] w-full rounded-[40px] transition-all duration-150 ease-in hover:scale-105">
                    <VideoComponent
                      thumbnail={perf.image_link ?? ""}
                      videoUrl={perf.video_link! ?? ""}
                      isPlaying={String(index) === currentPlayingVideo}
                    />
                    <div
                      className={cn(
                        "absolute inset-0 z-[60] rounded-[40px] bg-black/20",
                        String(index) === currentPlayingVideo ? "hidden" : ""
                      )}
                    />
                    <div
                      className={cn(
                        "absolute bottom-4 z-[90] flex items-center justify-center ltr:left-4 rtl:right-4",
                        String(index) === currentPlayingVideo ? "hidden" : ""
                      )}
                    >
                      <PlayButton
                        className="h-14 w-14"
                        onClick={() => {
                          setCurrentPlayingVideo(String(index));
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2 px-4">
                  <h2 className="text-2xl font-bold text-secondary 3xl:text-[34px] ltr:font-playfair">
                    {perf.title}
                  </h2>
                  <p className="text-base">{perf.description}</p>
                </div>
              </div>
            </div>
          </Reveal>
        </CarouselItem>
      ))}
    </CarouselContent>
  );
}
